<template>
  <ts-loading-banner :loading="loading">
    <div class="ts-space-y-2">
      <div class="row">
        <div class="col-md-12">
          <label class="required ts-text-xs">{{
            $t('employeeMission.employee')
          }}</label>
          <a-select
            show-search
            style="width: 100%"
            :default-active-first-option="false"
            :filter-option="false"
            :loading="fetch_employee"
            @search="handleEmployeeSearch"
            @change="onEmployeeSelect"
            v-model="model.employee_id"
            class="ts-w-full"
          >
            <a-select-option
              v-for="emp in employees"
              :key="emp.employee_id"
              :value="emp.employee_id"
            >
              {{ emp.employee_name_en }}
              {{ emp.pg_level ? `(PG-${emp.pg_level})` : '' }}
            </a-select-option>
          </a-select>
          <div class="validate" v-if="errors.has('employee_id')">
            {{ errors.first('employee_id') }}
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <label class="required ts-text-xs">{{
            $t('employeeMission.approvedBy')
          }}</label>
          <a-select
            show-search
            style="width: 100%"
            :loading="fetch_approved_by"
            @search="handleApprovedBySearch"
            v-model="model.approved_by"
            class="ts-w-full"
          >
            <a-select-option
              v-for="emp in approvedByEmployees"
              :key="emp.employee_id"
              :value="emp.employee_id"
            >
              {{ emp.employee_name_en }}
            </a-select-option>
          </a-select>
          <div class="validate" v-if="errors.has('approved_by')">
            {{ errors.first('approved_by') }}
          </div>
        </div>
        <div class="col-md-6">
          <label class="required ts-text-xs">{{
            $t('employeeMission.assignedBy')
          }}</label>
          <a-select
            show-search
            style="width: 100%"
            :loading="fetch_assigned_by"
            @search="handleAssignedBySearch"
            v-model="model.assigned_by"
            class="ts-w-full"
          >
            <a-select-option
              v-for="emp in assignedByEmployees"
              :key="emp.employee_id"
              :value="emp.employee_id"
            >
              {{ emp.employee_name_en }}
            </a-select-option>
          </a-select>
          <div class="validate" v-if="errors.has('assigned_by')">
            {{ errors.first('assigned_by') }}
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <label class="required ts-text-xs">{{
            $t('employeeMission.missionType')
          }}</label>
          <a-select
            style="width: 100%"
            v-model="model.mission_type_id"
            class="ts-w-full"
            @change="onMissionTypeChange"
          >
            <a-select-option
              v-for="type in missionTypes"
              :key="type.id"
              :value="type.id"
            >
              {{ type.mission_type }}
            </a-select-option>
          </a-select>
          <div class="validate" v-if="errors.has('mission_type_id')">
            {{ errors.first('mission_type_id') }}
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <label class="required ts-text-xs">{{
            $t('employeeMission.missionTitle')
          }}</label>
          <input
            v-model="model.mission_title"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.has('mission_title') }"
          />
          <div
            class="invalid-feedback ts-text-xs"
            v-if="errors.has('mission_title')"
          >
            {{ errors.first('mission_title') }}
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <label class="required ts-text-xs">{{
            $t('employeeMission.startDate')
          }}</label>
          <DatePicker
            class="ts-w-full"
            :value="model.start_date"
            @on-change="onChangeStart"
            :options="startDateOptions"
            format="yyyy-MM-dd"
            type="date"
            placeholder="YYYY-MM-DD"
            :class="{
              'ivu-form-item-error': errors.has('start_date')
            }"
          />
          <div
            class="ivu-form-item-error ts-text-red-600"
            v-if="errors.has('start_date')"
          >
            {{ errors.first('start_date') }}
          </div>
        </div>
        <div class="col-md-6">
          <label class="required ts-text-xs">{{
            $t('employeeMission.endDate')
          }}</label>
          <DatePicker
            class="ts-w-full"
            :value="model.end_date"
            :options="endDateOptions"
            @on-change="onChangeEnd"
            format="yyyy-MM-dd"
            type="date"
            placeholder="YYYY-MM-DD"
            :class="{
              'ivu-form-item-error': errors.has('end_date')
            }"
          />
          <div
            class="ivu-form-item-error ts-text-red-600"
            v-if="errors.has('end_date')"
          >
            {{ errors.first('end_date') }}
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-3">
          <label class="ts-text-xs text-muted">{{
            $t('employeeMission.totalDays')
          }}</label>
          <input
            :value="model.total_day"
            type="text"
            class="form-control"
            readonly
          />
        </div>
        <div class="col-md-3">
          <label class="ts-text-xs text-muted">{{
            $t('employeeMission.mealAllowance')
          }}</label>
          <input
            :value="model.meal_allowance"
            type="text"
            class="form-control"
            readonly
          />
        </div>
        <div class="col-md-3">
          <label class="ts-text-xs text-muted">{{
            $t('employeeMission.accommodationAllowance')
          }}</label>
          <input
            :value="model.accommodation_allowance"
            type="text"
            class="form-control"
            readonly
          />
        </div>
        <div class="col-md-3">
          <label class="ts-text-xs text-muted">{{
            $t('employeeMission.dayReturnAllowance')
          }}</label>
          <input
            :value="model.day_return_allowance"
            type="text"
            class="form-control"
            readonly
          />
        </div>
      </div>

      <div class="row mt-2">
        <div class="col-md-12">
          <label class="ts-text-xs">{{
            $t('employeeMission.advanceAmount')
          }}</label>
          <input
            v-model="model.advance_amount"
            type="number"
            class="form-control"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <label class="ts-text-xs">{{
            $t('employeeMission.missionDescription')
          }}</label>
          <textarea
            v-model.trim="model.mission_description"
            rows="3"
            class="form-control"
          ></textarea>
          <div class="validate" v-if="errors.has('mission_description')">
            {{ errors.first('mission_description') }}
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12 ts-mt-3 text-right">
          <ts-button class="mr-2" @click.prevent="() => $emit('cancel')">
            {{ $t('cancel') }}
          </ts-button>
          <ts-button
            color="primary"
            @click.prevent="isUpdate ? onUpdate() : onSave()"
            :waiting="waiting"
          >
            {{ isUpdate ? $t('update') : $t('save') }}
          </ts-button>
        </div>
      </div>
    </div>
  </ts-loading-banner>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapState } from 'vuex'
import _ from 'lodash'

export default {
  name: 'EmployeeMissionForm',
  data() {
    return {
      loading: false,
      errors: new Errors(),
      waiting: false,
      fetch_employee: false,
      fetch_approved_by: false,
      fetch_assigned_by: false,
      condition: null, // Stores MASTER DAILY RATES (e.g. 20)
      meal_eligible: false,
      accommodation_eligible: false,
      model: {
        id: null,
        employee_id: null,
        approved_by: null,
        assigned_by: null,
        mission_type_id: null,
        mission_title: null,
        advance_amount: 0,
        start_date: null,
        end_date: null,
        total_day: 0,
        mission_description: '',
        meal_allowance: 0,
        accommodation_allowance: 0,
        day_return_allowance: 0
      }
    }
  },
  computed: {
    ...mapState('mission/employeeMission', [
      'employees',
      'approvedByEmployees',
      'assignedByEmployees',
      'missionTypes',
      'edit_data'
    ]),
    isUpdate() {
      return !_.isEmpty(this.edit_data)
    },
    missionDays() {
      if (!this.model.start_date || !this.model.end_date) return 0
      const start = new Date(this.model.start_date)
      const end = new Date(this.model.end_date)
      start.setHours(0, 0, 0, 0)
      end.setHours(0, 0, 0, 0)

      // Calculate inclusive days (Mon to Tue = 2 days)
      const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1
      return diff > 0 ? diff : 0
    },
    missionNights() {
      // Hotel nights are usually Days - 1
      return this.missionDays > 1 ? this.missionDays - 1 : 0
    },

    startDateOptions() {
      return {
        disabledDate: date => {
          // Optional: Disable all dates before today
          // return date && date.valueOf() < Date.now() - 86400000;

          // If an end date is already picked, disable anything after it
          if (this.model.end_date) {
            return (
              date && date.valueOf() > new Date(this.model.end_date).valueOf()
            )
          }
          return false
        }
      }
    },

    // endDateOptions() {
    //   return {
    //     disabledDate: date => {
    //       // Disable all dates that are BEFORE the start date
    //       if (this.model.start_date) {
    //         return (
    //           date && date.valueOf() < new Date(this.model.start_date).valueOf()
    //         )
    //       }
    //       // If no start date is selected yet, you might want to disable everything
    //       // or just let them pick. Usually, we disable dates before "today".
    //       return date && date.valueOf() < Date.now() - 86400000
    //     }
    //   }
    // }
    endDateOptions() {
      return {
        disabledDate: date => {
          if (!this.model.start_date) {
            // If no start date is selected, disable dates before today
            return date && date.valueOf() < Date.now() - 86400000
          }

          const start = new Date(this.model.start_date)
          start.setHours(0, 0, 0, 0)

          // Rule for Mission Type 3: Long distance, must be at least +1 day
          if (this.model.mission_type_id === 3) {
            return date && date.valueOf() <= start.valueOf()
            // Note: "<=" means the same day is disabled.
          }

          // Rule for Mission Type 1 & 2: Same day is allowed
          return date && date.valueOf() < start.valueOf()
          // Note: "<" means only days BEFORE the start are disabled.
        }
      }
    }
  },
  watch: {
    'model.mission_type_id'(newType) {
      this.onMissionTypeChange()

      // Validation check: If type is 3(long distance) and dates are the same, clear the end date
      if (newType === 3 && this.model.start_date && this.model.end_date) {
        if (this.model.start_date === this.model.end_date) {
          this.model.end_date = null
        }
      }
    },
    // Recalculate whenever days change
    missionDays() {
      this.calculateAllowances()
    }
  },
  async mounted() {
    this.loading = true
    try {
      await Promise.all([
        this.$store.dispatch('mission/employeeMission/getEmployee', null),
        this.$store.dispatch(
          'mission/employeeMission/getApprovedByEmployees',
          null
        ),
        this.$store.dispatch(
          'mission/employeeMission/getAssignedByEmployees',
          null
        ),
        this.$store.dispatch('mission/employeeMission/getMissionTypes')
      ])
      await this.setEditData()
    } finally {
      this.loading = false
    }
  },
  methods: {
    async handleEmployeeSearch(search) {
      this.fetch_employee = true
      await this.$store.dispatch('mission/employeeMission/getEmployee', search)
      this.fetch_employee = false
    },
    async handleApprovedBySearch(search) {
      this.fetch_approved_by = true
      await this.$store.dispatch(
        'mission/employeeMission/getApprovedByEmployees',
        search
      )
      this.fetch_approved_by = false
    },
    async handleAssignedBySearch(search) {
      this.fetch_assigned_by = true
      await this.$store.dispatch(
        'mission/employeeMission/getAssignedByEmployees',
        search
      )
      this.fetch_assigned_by = false
    },

    async onEmployeeSelect(employeeId) {
      if (!employeeId) return
      try {
        const response = await this.$store.dispatch(
          'mission/employeeMission/getEmployeeCondition',
          employeeId
        )
        // Store the actual Daily Rates from HR Setup
        this.condition = response.condition
        if (response.message) this.$toasted.info(response.message)
        this.calculateAllowances()
      } catch (error) {
        this.$toasted.error(error.message)
      }
    },

    onMissionTypeChange() {
      const selected = this.missionTypes.find(
        t => t.id === this.model.mission_type_id
      )
      if (selected) {
        this.meal_eligible = selected.meal_eligible
        this.accommodation_eligible = selected.accommodation_eligible
      }
      this.calculateAllowances()
    },

    calculateAllowances() {
      // Don't calculate if we don't have the Daily Rates yet
      if (!this.condition || this.missionDays <= 0) return

      this.model.total_day = this.missionDays

      // 1. Meal Allowance: Rate * Days
      const dailyMealRate = parseFloat(this.condition.meal_allowance) || 0
      this.model.meal_allowance = this.meal_eligible
        ? dailyMealRate * this.missionDays
        : 0

      // 2. Accommodation: Rate * Nights
      const dailyRoomRate =
        parseFloat(this.condition.accommodation_allowance) || 0
      this.model.accommodation_allowance = this.accommodation_eligible
        ? dailyRoomRate * this.missionNights
        : 0

      // 3. Day Return: Flat Rate
      this.model.day_return_allowance =
        parseFloat(this.condition.day_return_allowance) || 0
    },

    async setEditData() {
      if (!_.isEmpty(this.edit_data)) {
        // Map saved database values to the model
        Object.assign(this.model, this.edit_data)

        // FETCH the Daily Rates from the server for this employee.
        // This fixes the issue where Total was being used as a Rate.
        await this.onEmployeeSelect(this.model.employee_id)

        // Set Eligibility flags for UI based on type
        this.onMissionTypeChange()
      }
    },

    onChangeStart(date) {
      this.model.start_date = date
    },
    onChangeEnd(date) {
      this.model.end_date = date
    },

    onSave() {
      this.waiting = true
      this.$store
        .dispatch('mission/employeeMission/store', this.model)
        .then(res => {
          this.$toasted.success(res.message)
          this.$emit('fetchData')
          this.$emit('cancel')
        })
        .catch(err => {
          this.errors = new Errors(err.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },

    onUpdate() {
      this.waiting = true
      this.$store
        .dispatch('mission/employeeMission/update', {
          id: this.model.id,
          data: this.model
        })
        .then(res => {
          this.$toasted.success(res.message)
          this.$emit('fetchData')
          this.$emit('cancel')
        })
        .catch(err => {
          this.errors = new Errors(err.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    }
  }
}
</script>
